import { Flex, Text, Icon } from "@chakra-ui/react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

interface PaginationControlProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PaginationControl: React.FC<PaginationControlProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // Function to render page numbers based on total pages
  const renderPageNumbers = () => {
    if (totalPages <= 6) {
      // If 6 or fewer pages, render all page numbers
      return Array.from({ length: totalPages }).map((_, index) => (
        <Flex
          key={index}
          align={"center"}
          justify={"center"}
          bg={currentPage === index + 1 ? "#FAFAFA" : "white"}
          w={"40px"}
          h={"40px"}
          borderRadius={"8px"}
          cursor="pointer"
          onClick={() => onPageChange && onPageChange(index + 1)}
        >
          <Text
            fontWeight={600}
            fontSize={"16px"}
            color={currentPage === index + 1 ? "#101720" : "#667085"}
          >
            {index + 1}
          </Text>
        </Flex>
      ));
    } else {
      // If more than 6 pages, render first 3, ellipsis, and last 3
      const pageNumbers = [];

      // First 3 pages
      for (let i = 1; i <= 3; i++) {
        pageNumbers.push(
          <Flex
            key={i}
            align={"center"}
            justify={"center"}
            bg={currentPage === i ? "#FAFAFA" : "white"}
            w={"40px"}
            h={"40px"}
            borderRadius={"8px"}
            cursor="pointer"
            onClick={() => onPageChange && onPageChange(i)}
          >
            <Text
              fontWeight={600}
              fontSize={"16px"}
              color={currentPage === i ? "#101720" : "#667085"}
            >
              {i}
            </Text>
          </Flex>
        );
      }

      // Ellipsis
      pageNumbers.push(
        <Flex
          key="ellipsis"
          align={"center"}
          justify={"center"}
          w={"40px"}
          h={"40px"}
        >
          <Text fontWeight={600} fontSize={"16px"} color={"#667085"}>
            ...
          </Text>
        </Flex>
      );

      // Last 3 pages
      for (let i = totalPages - 2; i <= totalPages; i++) {
        pageNumbers.push(
          <Flex
            key={i}
            align={"center"}
            justify={"center"}
            bg={currentPage === i ? "#FAFAFA" : "white"}
            w={"40px"}
            h={"40px"}
            borderRadius={"8px"}
            cursor="pointer"
            onClick={() => onPageChange && onPageChange(i)}
          >
            <Text
              fontWeight={600}
              fontSize={"16px"}
              color={currentPage === i ? "#101720" : "#667085"}
            >
              {i}
            </Text>
          </Flex>
        );
      }

      return pageNumbers;
    }
  };

  return (
    <Flex
      align={"center"}
      justify={"space-between"}
      mb="93px"
      fontFamily={"Raleway"}
    >
      <Flex
        borderWidth={"1px"}
        borderColor="#E7E8E9"
        borderRadius={{ base: "8px", lg: "30px" }}
        bg="white"
        py={{ base: "10px", lg: "8px" }}
        px={{ base: "10px", lg: "14px" }}
        gap={"2px"}
        align={"center"}
        cursor="pointer"
        onClick={() =>
          currentPage > 1 && onPageChange && onPageChange(currentPage - 1)
        }
      >
        <Icon size="lg" color={"#101828"}>
          <MdKeyboardArrowLeft />
        </Icon>

        <Text
          fontWeight={600}
          fontSize={"16px"}
          color={"#344054"}
          display={{ base: "none", lg: "block" }}
        >
          Previous
        </Text>
      </Flex>

      <Flex align={"center"} display={{ base: "none", lg: "flex" }}>
        {renderPageNumbers()}
      </Flex>

      <Text
        display={{ base: "block", lg: "none" }}
        fontFamily={"Raleway"}
        fontWeight={600}
        color={"#667085"}
        fontSize={"13px"}
      >
        Page {currentPage} of {totalPages}
      </Text>

      <Flex
        borderWidth={"1px"}
        borderColor="#E7E8E9"
        borderRadius={{ base: "8px", lg: "30px" }}
        bg="white"
        py={{ base: "10px", lg: "8px" }}
        px={{ base: "10px", lg: "14px" }}
        gap={"2px"}
        align={"center"}
        cursor="pointer"
        onClick={() =>
          currentPage < totalPages &&
          onPageChange &&
          onPageChange(currentPage + 1)
        }
      >
        <Text
          fontWeight={600}
          fontSize={"16px"}
          color={"#344054"}
          display={{ base: "none", lg: "block" }}
        >
          Next
        </Text>

        <Icon size="lg" color={"#101828"}>
          <MdKeyboardArrowRight />
        </Icon>
      </Flex>
    </Flex>
  );
};
